/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback } from 'react';
import Header, { BreadcrumbItem } from '../components/Header';
import ContentReader from '../components/ContentReader';
import VideoModal from '../components/VideoModal';
import HistoryButton from '../components/HistoryButton';
import StepHelpButton from '../components/StepHelpButton';
import HistoryModal from '../components/HistoryModal';
import Step8ListModal from '../components/Step8ListModal';
import { progressService, ProgressItem, JourneyProgress } from '../services/progressService';
import { InventoryService } from '../services/inventoryService';
import { useDialog } from '../context/DialogContext';
import { useStepWorkspaceRevision } from '../context/StepWorkspaceContext';
import { useAuthErrorHandler } from '../hooks/useAuthErrorHandler';
import { AuthErrorDisplay } from '../components/AuthErrorDisplay';
import styles from '../styles/stepCommon.module.css';
import StepActions, { ActionItem } from '../components/StepActions';
import StepLoginPrompt from '../components/StepLoginPrompt';
import StepShareDialog from '../components/StepShareDialog';
import { useStepShareDialog } from '../hooks/useStepShareDialog';
import { useRelocalizeStepActions } from '../hooks/useRelocalizeStepActions';
import { withShareButton } from '../utils/stepShareUtils';
import { mergeStepActions, stepDataWithActions, buildStepDataWithActions } from '../utils/mergeStepActions';
import { perfMark, perfSummary, perfTimeAsync } from '../utils/perfTimer';
import { getStepActionSchema } from '../config/stepActionSchemas';
import { STEP_UI } from '../config/uiStrings';
import { getStepActionLabel } from '../config/stepActionLabels';
import { getStepActionDescription } from '../config/stepActionDescriptions';
import {
  getActionsIntro,
  getActionsToCompleteTitle,
  getCongratsBody,
  getCongratsHeading,
  getGuidanceModalTitle,
  getNextStepNavLabel,
  getPrevStepNavLabel,
  getRefreshPageLabel,
  getStepBreadcrumbs,
  getStepLoadingMessage,
  getStepPageQuote,
  getStepPageTitle,
  getVideoGuideTitle,
} from '../config/stepPageCopy';
import { t } from '../i18n/translate';

interface Step8Props {
  user?: any;
  onLogin: () => void;
  onLogout: () => void;
}

const Step8: React.FC<Step8Props> = ({ user, onLogin, onLogout }) => {
  const stepWorkspaceRevision = useStepWorkspaceRevision();
  const { showAlert } = useDialog();
  const [isContentReaderOpen, setIsContentReaderOpen] = useState(false);
  const [isGuidanceOpen, setIsGuidanceOpen] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [isListModalOpen, setIsListModalOpen] = useState(false);
  const [listCount, setListCount] = useState(0);
  const [completedCount, setCompletedCount] = useState(0);
  const [amendsPersons, setAmendsPersons] = useState<any[]>([]);
  const [collapsedActions, setCollapsedActions] = useState<Set<number>>(new Set());
  // Define the original action structure
  const originalActions: ActionItem[] = [
    {
      id: 1,
      title: getStepActionLabel(8, 1),
      description: getStepActionDescription(8, 1),
      completed: false,
      hasButton: true,
      buttonText: STEP_UI.READ_GREEN_BOOK,
      buttonAction: () => setIsContentReaderOpen(true)
    },
    {
      id: 2,
      title: getStepActionLabel(8, 2),
      description: getStepActionDescription(8, 2),
      completed: false,
      buttons: [
        {
          text: STEP_UI.READ_GUIDANCE,
          action: () => setIsGuidanceOpen(true),
          variant: 'primary'
        },
        {
          text: STEP_UI.WATCH_VIDEO,
          action: () => setIsVideoModalOpen(true),
          variant: 'secondary'
        }
      ]
    },
    {
      id: 3,
      title: getStepActionLabel(8, 3),
      description: getStepActionDescription(8, 3),
      completed: false,
      hasButton: true,
      buttonText: STEP_UI.OPEN_AMENDS_LIST,
      buttonAction: () => setIsListModalOpen(true)
    },
    {
      id: 4,
      title: getStepActionLabel(8, 4),
      description: getStepActionDescription(8, 4),
      completed: false
    },
    {
      id: 5,
      title: getStepActionLabel(8, 5),
      description: getStepActionDescription(8, 5),
      completed: false
    }
  ];

  const [actions, setActions] = useState<ActionItem[]>(originalActions);
  useRelocalizeStepActions(originalActions, setActions);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [stepData, setStepData] = useState<any>(null);
  const [isOptedOut, setIsOptedOut] = useState(false);
  const { authError, errorMessage, handleError, clearError } = useAuthErrorHandler();

  const getShareContext = useCallback(() => ({ amendsPersons }), [amendsPersons]);
  const { openShareDialog, shareDialogProps } = useStepShareDialog(8, user, actions, getShareContext);

  const breadcrumbs: BreadcrumbItem[] = getStepBreadcrumbs(8);

  // Check if a person entry is complete (same logic as Step8ListModal)
  const isPersonComplete = (person: any): boolean => {
    if (!person || typeof person !== 'object') return false;
    
    const name = (person.name && typeof person.name === 'string') ? person.name.trim() : '';
    const note = (person.note && typeof person.note === 'string') ? person.note.trim() : '';
    const timing = (person.timing && typeof person.timing === 'string') ? person.timing.trim() : '';
    const form = (person.form && typeof person.form === 'string') ? person.form.trim() : '';
    
    return !!(
      name.length > 0 && 
      note.length > 0 &&
      timing.length > 0 &&
      form.length > 0
    );
  };

  // Load list count and completion status
  const loadListCount = async () => {
    if (!user) {
      setListCount(0);
      setCompletedCount(0);
      return;
    }
    
    try {
      const items = await InventoryService.getInventory('step8-persons', 'default');
      // Filter and map items the same way as Step8ListModal does
      const personItems = items
        .filter(item => item != null && typeof item === 'object' && item._type !== 'completion_status')
        .map(item => ({
          name: item.name || '',
          note: item.note || '',
          timing: item.timing || undefined,
          form: item.form || undefined
        }));
      setListCount(personItems.length);
      
      // Count completed entries using the same logic
      const completed = personItems.filter(item => isPersonComplete(item)).length;
      setCompletedCount(completed);
      setAmendsPersons(personItems);
    } catch (error) {
      // If no list exists yet, counts are 0
      setListCount(0);
      setCompletedCount(0);
    }
  };

  const applyLoadedStepProgress = async (
    existingProgress: ProgressItem | null,
    journeyProgress: JourneyProgress | null
  ) => {
    if (journeyProgress) {
      setIsOptedOut(journeyProgress.opted_out);
    }

    if (existingProgress) {
      const savedData = existingProgress.data || {};
      setStepData(existingProgress);

      if (savedData.actions) {
        const merged = mergeStepActions(8, originalActions, savedData, !!existingProgress.completed);
        setActions(merged.actions);
        if (merged.migrationApplied) {
          await progressService.updateStepProgress({
            program: 'SAA',
            track: '12-steps',
            step_number: 8,
            step_title: 'Step Eight',
            completed: !!existingProgress.completed,
            data: buildStepDataWithActions(merged.actions, merged.actionSchemaVersion, savedData),
          });
        }
      }
    }
  };

  const refreshStepData = async () => {
    if (!user) {
      setIsInitializing(false);
      return;
    }

    setIsInitializing(true);

    try {
      perfMark('Step8', 'refreshStepData start');
      const [{ steps, journeyProgress }] = await Promise.all([
        perfTimeAsync('Step8 loadStepPagesProgress (parallel)', () =>
          progressService.loadStepPagesProgress('SAA', '12-steps', [8], { includeJourney: true })
        ),
        loadListCount(),
      ]);

      await applyLoadedStepProgress(steps[8] ?? null, journeyProgress);
      clearError();
      perfSummary('Step Eight page');
    } catch (err: any) {
      console.error('Error loading step progress:', err);
      handleError(err, t('step.page.error.load'));
    } finally {
      setIsInitializing(false);
    }
  };

  useEffect(() => {
    refreshStepData();
  }, [user, stepWorkspaceRevision]);

  const handleHistoryStepUpdated = () => {
    setIsHistoryModalOpen(false);
    if (user) {
      void refreshStepData();
    }
  };

  const getCurrentActionIndex = () => {
    return actions.findIndex(action => !action.completed);
  };

  const isActionAvailable = (actionIndex: number) => {
    if (actionIndex === 0) return true;
    return actions.slice(0, actionIndex).every(a => a.completed);
  };

  // Validate action 3 completion - requires at least 1 completed person entry
  const validateAction3Completion = () => {
    return completedCount > 0;
  };

  // Check if action 3 can be completed (list must have at least one completed person)
  const canCompleteAction3 = () => {
    return validateAction3Completion();
  };

  // Check if any action can be completed
  const canCompleteAction = (actionId: number) => {
    if (actionId === 3) {
      return canCompleteAction3();
    }
    if (actionId === 4 || actionId === 5) {
      const action3 = actions.find(a => a.id === 3);
      return action3 ? action3.completed : false;
    }
    return true; // Actions 1 and 2 can always be completed
  };

  const handleActionChange = async (updatedActions: ActionItem[]) => {
    // Check if action 3 is being marked complete
    const action3 = updatedActions.find(a => a.id === 3);
    const currentAction3 = actions.find(a => a.id === 3);
    
    if (action3 && !currentAction3?.completed && action3.completed) {
      // Action 3 is being marked complete - validate
      if (!validateAction3Completion()) {
        showAlert({
          message: t('step.8.alert.complete_one_person'),
          variant: 'warning'
        });
        return;
      }
    }
    
    // For other actions, save to backend
    if (!user) {
      handleError(new Error('User not logged in'), t('step.page.error.sign_in'));
      return;
    }
    
    try {
      // Update local state
      setActions(updatedActions);
      
      // Save to backend
      const allCompleted = updatedActions.every(action => action.completed);
      const updatedStepData = await progressService.updateStepProgress({
        program: 'SAA',
        track: '12-steps',
        step_number: 8,
        step_title: 'Step Eight',
        completed: allCompleted,
        data: stepDataWithActions(updatedActions, getStepActionSchema(8).actionSchemaVersion)
      });
      
      // Update step data for history modal
      if (updatedStepData && updatedStepData.step) {
        setStepData(updatedStepData.step);
      }
      
      // Update status note
      const completedCount = updatedActions.filter(a => a.completed).length;
      await progressService.updateStatus({
        program: 'SAA',
        track: '12-steps',
        status_note: `Working Step Eight - ${completedCount} of 5 actions completed`,
        progress_milestone: allCompleted ? 'step8_completed' : 'step8_in_progress'
      });
    } catch (err: any) {
      console.error('Error updating action progress:', err);
      handleError(err, t('step.page.error.save_progress'));
    }
  };

  // Check if all actions are completed
  const allCompleted = actions.every(action => action.completed);

  return (
    <div className={styles.stepPage}>
      <Header 
        breadcrumbs={breadcrumbs}
        user={user}
        onLogout={onLogout}
        onLogin={onLogin}
      />
      
      <main className={styles.mainContent}>
        <div className={styles.pageHeader}>
          <div className={styles.stepTitleContainer}>
            <h1 className={styles.stepTitle}>{getStepPageTitle(8)}</h1>
            <StepHelpButton stepKey="app" />
            {stepData && (
              <HistoryButton
                stepNumber={8}
                stepTitle={getStepPageTitle(8)}
                stepData={stepData}
                isOptedOut={isOptedOut}
                onStepUpdated={() => setIsHistoryModalOpen(true)}
              />
            )}
          </div>
          <p className={styles.stepSubtitle}>{getStepPageQuote(8)}
          </p>
        </div>

        {errorMessage && (
          <div className={styles.errorMessage}>
            {authError ? (
              <AuthErrorDisplay
                error={errorMessage}
                onLogin={onLogin}
                onRefresh={() => window.location.reload()}
              />
            ) : (
              <>
                <p>{errorMessage}</p>
                <button onClick={() => window.location.reload()}>{getRefreshPageLabel()}</button>
              </>
            )}
          </div>
        )}

        {!user && !isInitializing && !errorMessage && (
          <StepLoginPrompt onLogin={onLogin} />
        )}

        {user && isInitializing && !errorMessage && (
          <div className={styles.loadingMessage}>
            <p>{getStepLoadingMessage()}</p>
          </div>
        )}

        {user && !isInitializing && !errorMessage && (
          <div className={styles.actionsContainer}>
            <h2 className={styles.actionsTitle}>{getActionsToCompleteTitle(8)}</h2>
            <p className={styles.actionsDescription}>{getActionsIntro()}</p>
            <p style={{ marginTop: '0.5rem', color: '#6b7280', fontSize: '0.875rem' }}>
              {t('step.8.helper.requires_action3')}
            </p>
            
            <StepActions
              actions={withShareButton(8, actions, openShareDialog)}
              onActionChange={handleActionChange}
              stepNumber={8}
              canCompleteAction={canCompleteAction}
              renderActionContent={(action, index) => {
                if (action.id === 3) {
                  return (
                    <div style={{ 
                      marginTop: '0.5rem', 
                      color: '#6b7280', 
                      fontSize: '0.875rem',
                      paddingLeft: '56px' // Align with button: actionNumber (32px) + margin-right (0.75rem ≈ 12px) + gap (0.75rem ≈ 12px) = 56px
                    }}>
                      {listCount === 0 ? (
                        <span>{t('step.8.helper.empty')}</span>
                      ) : (
                        <span>
                          {t('step.8.helper.count', {
                            count: listCount,
                            personWord:
                              listCount === 1
                                ? t('step.8.helper.person')
                                : t('step.8.helper.persons'),
                            completed: completedCount,
                          })}
                        </span>
                      )}
                    </div>
                  );
                }
                return null;
              }}
            />

            {allCompleted && (
              <>
                <div className={styles.completionMessage}>
                  <h3>{getCongratsHeading()}</h3>
                  <p>{getCongratsBody(8)}</p>
                </div>
                <div className={styles.stepNavigation}>
                  <div className={styles.navigationLinks}>
                    <a href="/saa/12-steps/step-7" className={styles.prevStepLink}>
                      {getPrevStepNavLabel(8)}
                    </a>
                    <span></span>
                    <a href="/saa/12-steps/step-9" className={styles.nextStepLink}>
                      {getNextStepNavLabel(8)}
                    </a>
                  </div>
                </div>
              </>
            )}

            {!allCompleted && (
              <div className={styles.stepNavigation}>
                <div className={styles.navigationLinks}>
                  <a href="/saa/12-steps/step-7" className={styles.prevStepLink}>
                    {getPrevStepNavLabel(8)}
                  </a>
                  <span></span>
                  <a href="/saa/12-steps/step-9" className={styles.nextStepLink}>
                    {getNextStepNavLabel(8)}
                  </a>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {/* ContentReader for Green Book */}
      <ContentReader
        isOpen={isContentReaderOpen}
        onClose={() => setIsContentReaderOpen(false)}
        filePath="/content/010_CHAPTER_THREE.md"
        startTag="<<step-eight start>>"
        endTag="<<step-eight end>>"
      />

      {/* ContentReader for How to Work Step Eight */}
      <ContentReader
        isOpen={isGuidanceOpen}
        onClose={() => setIsGuidanceOpen(false)}
        filePath="/content/Step_8_Guidance.md"
        startTag="<<working-step-eight start>>"
        endTag="<<working-step-eight end>>"
        title={getGuidanceModalTitle(8)}
      />

      {/* Video Modal for Step Eight */}
      <VideoModal
        isOpen={isVideoModalOpen}
        onClose={() => setIsVideoModalOpen(false)}
        videoId="vX1pg5ql1lE"
        title={getVideoGuideTitle(8)}
      />

      {/* History Modal */}
      <HistoryModal
        isOpen={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        stepNumber={8}
        stepTitle={getStepPageTitle(8)}
        stepData={stepData}
        onStepUpdated={handleHistoryStepUpdated}
      />

      {/* Step 8 List Modal */}
      <Step8ListModal
        isOpen={isListModalOpen}
        onClose={() => setIsListModalOpen(false)}
        user={user}
        onListUpdate={() => {
          loadListCount();
        }}
      />

      <StepShareDialog {...shareDialogProps} />
    </div>
  );
};

export default Step8; 
